import styled from "@emotion/styled";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { RecentActivitySection } from "../contacts.types";

const CardAccordion = styled(Accordion)`
  background-color: ${(p) => p.theme.colors.background};
  border-radius: 0.5rem;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  width: 100%;
  margin: 0;

  &.Mui-expanded {
    margin: 0;
    background-color: ${(p) => p.theme.colors.background};
  }

  & .MuiAccordionSummary-root,
  & .MuiAccordionDetails-root {
    background-color: ${(p) => p.theme.colors.background};
  }

  &::before {
    display: none;
  }
`;

const CardHeaderTitle = styled(Typography)`
  font-weight: 600;
  font-size: ${(p) => p.theme.text.fontSizes.l};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(p) => p.theme.text.colors.brand};
`;

const Chevron = styled(ExpandMoreIcon)`
  color: ${(p) => p.theme.text.colors.secondary};
`;

const CardSummary = styled(AccordionSummary)`
  padding: 0 1rem;
  background-color: ${(p) => p.theme.colors.background};

  & .MuiAccordionSummary-content {
    margin: 1rem 0;
  }

  & .MuiAccordionSummary-expandIconWrapper {
    color: ${(p) => p.theme.text.colors.secondary};
  }

  &:hover {
    opacity: 0.9;
  }
`;

const CardDetails = styled(AccordionDetails)`
  padding: 0;
  border-top: 1px solid ${(p) => p.theme.colors.border2};
  background-color: ${(p) => p.theme.colors.background};
`;

const HalvesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  min-height: 120px;

  ${(p) => p.theme.media.tabletDown} {
    grid-template-columns: 1fr;
  }
`;

const Half = styled.div`
  padding: 1rem;
  border-right: 1px solid ${(p) => p.theme.colors.border2};

  &:last-of-type {
    border-right: none;
  }

  ${(p) => p.theme.media.tabletDown} {
    border-right: none;
    border-bottom: 1px solid ${(p) => p.theme.colors.border2};

    &:last-of-type {
      border-bottom: none;
    }
  }
`;

const HalfTitle = styled(Typography)`
  font-weight: 600;
  font-size: ${(p) => p.theme.text.fontSizes.m};
  margin-bottom: 0.5rem;
  color: ${(p) => p.theme.text.colors.brand};
`;

const ActivityList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: ${(p) => p.theme.text.fontSizes.m};
  color: ${(p) => p.theme.text.colors.secondary};
`;

const ActivityItem = styled.li`
  margin-bottom: 0.25rem;
  list-style-type: disc;
`;

const ShowMoreLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: 0.5rem;
  font-size: ${(p) => p.theme.text.fontSizes.s};
  color: ${(p) => p.theme.colors.primary};
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
`;

type RecentActivityBlockProps = {
  sections: RecentActivitySection[];
};

export const RecentActivityBlock = (props: RecentActivityBlockProps) => {
  const { sections } = props;

  if (sections.length === 0) return null;

  return (
    <section>
      <CardAccordion defaultExpanded disableGutters elevation={0}>
        <CardSummary expandIcon={<Chevron fontSize="small" />}>
          <CardHeaderTitle variant="h6">
            <AccessTimeIcon fontSize="small" />
            Recent Activity
          </CardHeaderTitle>
        </CardSummary>
        <CardDetails>
          <HalvesGrid>
            {sections.map((section) => (
              <Half key={section.id}>
                <HalfTitle variant="subtitle2">{section.title}</HalfTitle>
                <ActivityList>
                  {section.items.map((item) => (
                    <ActivityItem key={item.id}>
                      <strong>{item.date}:</strong> {item.description}
                    </ActivityItem>
                  ))}
                </ActivityList>
                {section.showMoreLabel && (
                  <ShowMoreLink type="button">
                    {section.showMoreLabel}
                    <ExpandMoreIcon fontSize="small" />
                  </ShowMoreLink>
                )}
              </Half>
            ))}
          </HalvesGrid>
        </CardDetails>
      </CardAccordion>
    </section>
  );
};
